/*! Spider Effect v1.0
 * A procedurally rendered, top-down "spider walking on your monitor glass".
 *  - No dependencies, no external assets, no WebGL required.
 *  - 8 independently animated legs (2-bone IK + tetrapod gait, planted feet).
 *  - Soft contact shadows, world-fixed lighting, hairy legs, glossy eyes.
 *  - Lives in a small, GPU-moved canvas: pointer-events:none, never blocks the page.
 *  - Halloween greeting "🎃 HAPPY HALLOWEEN! 🕷️" that follows the spider every frame.
 *
 * Usage (simplest):   <script src="spider-effect.js" defer></script>
 * Manual start:       <script src="spider-effect.js" data-manual defer></script>
 *                     then  SpiderEffect.init({ delayMin: 5, delayMax: 12 });
 *
 * Public API:  SpiderEffect.init(opts) · .show() · .hide() · .enable() · .disable() · .destroy()
 * Global opts: window.SpiderEffectConfig = { ... }  (set BEFORE loading the script)
 */
(function (global, doc) {
  'use strict';
  if (global.SpiderEffect) return;

  /* ------------------------------------------------------------------ */
  /* Options                                                            */
  /* ------------------------------------------------------------------ */
  var DEFAULTS = {
    delayMin: 5,
    delayMax: 12,
    repeat: false,
    repeatDelayMin: 30,
    repeatDelayMax: 75,
    roamMin: 14,
    roamMax: 24,
    sizeCm: 4,
    respectReducedMotion: true,
    oncePerSession: false,
    showToggle: false,
    storageKey: 'spider-effect',
    zIndex: 2147483647,
    abdomenImage: null,
    prosomaImage: null,
    autoStart: true
  };

  /* ------------------------------------------------------------------ */
  /* Helpers                                                            */
  /* ------------------------------------------------------------------ */
  var TAU = Math.PI * 2, PI = Math.PI;

  function rand(a, b) {
    return a + Math.random() * (b - a);
  }

  function clamp(v, a, b) {
    return v < a ? a : v > b ? b : v;
  }

  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  function smooth(t) {
    return t * t * (3 - 2 * t);
  }

  function angDiff(a, b) {
    var d = (b - a) % TAU;
    if (d > PI) d -= TAU;
    else if (d < -PI) d += TAU;
    return d;
  }

  function mulberry(seed) {
    return function () {
      seed |= 0;
      seed = seed + 0x6D2B79F5 | 0;
      var t = Math.imul(seed ^ seed >>> 15, 1 | seed);
      t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }

  function store(k, v, sess) {
    try {
      var s = sess ? sessionStorage : localStorage;
      if (v === null) s.removeItem(k);
      else s.setItem(k, v);
    } catch (e) {}
  }

  function load(k, sess) {
    try {
      return (sess ? sessionStorage : localStorage).getItem(k);
    } catch (e) {
      return null;
    }
  }

  function mk(w, h) {
    var c = doc.createElement('canvas');
    c.width = w;
    c.height = h;
    return c;
  }

  /* ------------------------------------------------------------------ */
  /* Anatomy                                                            */
  /* ------------------------------------------------------------------ */
  var HIPS = [
    [0.150, 0.040],
    [0.105, 0.050],
    [0.055, 0.052],
    [0.005, 0.045]
  ];

  var REST = [
    [0.40, 0.21],
    [0.28, 0.40],
    [-0.10, 0.43],
    [-0.40, 0.27]
  ];

  var LEN = [0.50, 0.50, 0.54, 0.62];

  var HAIRCFG = [
    { n: 14, l0: 0.008, l1: 0.016 },
    { n: 18, l0: 0.012, l1: 0.026 },
    { n: 7, l0: 0.006, l1: 0.012 }
  ];

  var C_DARK = '#555b63';
  var C_MID = '#aeb6c2';
  var C_LITE = '#f2f5fa';

  /* ------------------------------------------------------------------ */
  /* State                                                              */
  /* ------------------------------------------------------------------ */
  var cfg, S = 0, B = 0, dpr = 1, W = 0, H = 0;
  var vis, vctx, buf, bctx, abdC, proC, lightA, lightB, hairs = [];
  var sp = null, raf = 0, last = 0, running = false, paused = false;

  // Mouse position used by the spider's look-at behavior.
  var mouseX = -10000;
  var mouseY = -10000;

  doc.addEventListener('mousemove', function (e) {
    mouseX = e.clientX;
    mouseY = e.clientY;
  }, { passive: true });

  doc.addEventListener('mouseleave', function () {
    mouseX = -10000;
    mouseY = -10000;
  });

  var timer = 0;
  var destroyed = false;
  var built = false;
  var toggleBtn = null;
  var resizeT = 0;
  var inited = false;

  // Halloween greeting (follows the spider)
  var GREET_TEXT = '\uD83C\uDF83 HAPPY HALLOWEEN! \uD83D\uDD77\uFE0F';
  var greet = null;
  var greetStyle = null;
  var greetW = 0;
  var greetH = 0;

  /* ------------------------------------------------------------------ */
  /* DOM / sizing                                                        */
  /* ------------------------------------------------------------------ */
  function ensureDom() {
    if (vis) return;

    var st = doc.createElement('style');

    st.textContent =
      '.spx-canvas{position:fixed;left:0;top:0;inset:auto;margin:0;padding:0;border:0;' +
      'background:transparent;overflow:visible;pointer-events:none;user-select:none;-webkit-user-select:none;' +
      'will-change:transform;visibility:hidden;z-index:' + cfg.zIndex + '}';

    doc.head.appendChild(st);

    vis = doc.createElement('canvas');
    vis.className = 'spx-canvas';
    vis.setAttribute('aria-hidden', 'true');
    vis.setAttribute('role', 'presentation');

    doc.body.appendChild(vis);

    // Float above dialogs and modals where supported.
    try {
      if (vis.showPopover) {
        vis.setAttribute('popover', 'manual');
        vis.showPopover();
      }
    } catch (e) {}

    vctx = vis.getContext('2d');

    buf = doc.createElement('canvas');
    bctx = buf.getContext('2d');

    ensureGreeting();
  }

  // Creates the Halloween greeting element (own <style> + own element).
  // It is created AFTER the canvas so that, in the top layer, it is stacked
  // above the spider rather than behind it.
  function ensureGreeting() {
    if (greet) return;

    greetStyle = doc.createElement('style');

    greetStyle.textContent =
      '.spx-greet{position:fixed;left:0;top:0;right:auto;bottom:auto;margin:0;padding:0;border:0;' +
      'background:transparent;overflow:visible;width:max-content;height:auto;' +
      'max-width:calc(100vw - 8px);white-space:nowrap;text-align:center;' +
      'pointer-events:none;user-select:none;-webkit-user-select:none;' +
      'will-change:transform;visibility:hidden;opacity:0;transition:opacity .2s linear;' +
      'z-index:' + cfg.zIndex + ';' +
      'font:800 10px/1.2 system-ui,-apple-system,"Segoe UI",Roboto,Arial,sans-serif;' +
      'letter-spacing:.04em;color:#ff8a1f;' +
      'text-shadow:0 0 4px rgba(255,138,31,.9),0 0 10px rgba(255,100,0,.55),0 1px 2px rgba(0,0,0,.65)}' +
      '@media (max-width:480px){.spx-greet{font-size:9px;letter-spacing:.02em}}';

    doc.head.appendChild(greetStyle);

    greet = doc.createElement('div');
    greet.className = 'spx-greet';
    greet.setAttribute('aria-hidden', 'true');
    greet.setAttribute('role', 'presentation');
    greet.textContent = GREET_TEXT;

    doc.body.appendChild(greet);

    // Same top-layer trick as the canvas; shown after it, so it sits on top.
    try {
      if (greet.showPopover) {
        greet.setAttribute('popover', 'manual');
        greet.showPopover();
      }
    } catch (e) {}

    greetW = 0;
    greetH = 0;
  }

  // Positions the greeting directly above the spider, using the exact same
  // (rounded) animation coordinates as the canvas. Called on every frame.
  function positionGreeting(ix, iy) {
    if (!greet || !sp) return;

    if (!greetW || !greetH) {
      greetW = greet.offsetWidth || 150;
      greetH = greet.offsetHeight || 14;
    }

    // Sit just above the leg span, centred on the spider's body.
    var gap = 0.5 * S + 4;
    var left = ix - greetW / 2;
    left = clamp(left, 4, Math.max(4, W - greetW - 4));

    var bottom = iy - gap;
    var top = bottom - greetH;
    if (top < 2) top = 2;

    greet.style.transform =
      'translate3d(' + left + 'px,' + top + 'px,0)';

    // Only show while the spider's centre is on screen.
    greet.style.opacity =
      (sp.x > 0 && sp.x < W && sp.y > 0 && sp.y < H) ? '1' : '0';

    greet.style.visibility = 'visible';
  }

  function computeSize() {
    W = global.innerWidth;
    H = global.innerHeight;

    var spanPx = Math.min(
      cfg.sizeCm * 37.8,
      0.36 * Math.min(W, H)
    );

    spanPx = Math.max(spanPx, 84);

    S = spanPx / 0.9;
    B = Math.ceil(S * 1.95 / 2) * 2;
    dpr = Math.min(global.devicePixelRatio || 1, 2);
  }

  function build() {
    computeSize();

    vis.width = vis.height = B * dpr;
    vis.style.width = vis.style.height = B + 'px';

    buf.width = buf.height = B * dpr;

    bake();

    var c = B * dpr / 2;
    var k = S * dpr;

    lightA = bctx.createRadialGradient(
      c - 0.16 * k,
      c - 0.18 * k,
      0,
      c - 0.10 * k,
      c - 0.12 * k,
      0.55 * k
    );

    lightA.addColorStop(0, 'rgba(255,238,215,0.24)');
    lightA.addColorStop(1, 'rgba(255,238,215,0)');

    lightB = bctx.createLinearGradient(
      c - 0.3 * k,
      c - 0.3 * k,
      c + 0.45 * k,
      c + 0.45 * k
    );

    lightB.addColorStop(0, 'rgba(0,0,0,0)');
    lightB.addColorStop(1, 'rgba(0,0,0,0.32)');

    built = true;
  }

  /* ------------------------------------------------------------------ */
  /* Procedural body textures                                            */
  /* ------------------------------------------------------------------ */
  function strokeArr(g, a, col) {
    g.strokeStyle = col;
    g.beginPath();

    for (var i = 0; i < a.length; i += 4) {
      g.moveTo(a[i], a[i + 1]);
      g.lineTo(a[i + 2], a[i + 3]);
    }

    g.stroke();
  }

  function hairBurst(g, rng, n, cx, cy, rx, ry, len, ang0, spread) {
    var dk = [], lt = [], i;

    for (i = 0; i < n; i++) {
      var a = rng() * TAU;
      var r = Math.sqrt(rng()) * 0.95;

      var x = cx + Math.cos(a) * r * rx;
      var y = cy + Math.sin(a) * r * ry;

      var d = ang0 + (rng() - 0.5) * spread + ((y - cy) / ry) * 0.6;
      var l = len * (0.5 + rng());

      (rng() < 0.45 ? lt : dk).push(
        x, y,
        x + Math.cos(d) * l,
        y + Math.sin(d) * l
      );
    }

    g.lineWidth = Math.max(0.6, 0.55 * dpr);
    g.lineCap = 'round';

    strokeArr(g, dk, 'rgba(8,5,3,0.55)');
    strokeArr(g, lt, 'rgba(150,115,76,0.38)');
  }

  function abdPath(g, w, h) {
    var cx = w / 2;
    var cy = h / 2;
    var rx = w * 0.47;
    var ry = h * 0.46;

    g.beginPath();
    g.moveTo(cx + rx, cy);

    g.bezierCurveTo(
      cx + rx, cy - ry * 0.75,
      cx + rx * 0.25, cy - ry * 1.05,
      cx - rx * 0.2, cy - ry * 0.92
    );

    g.bezierCurveTo(
      cx - rx * 0.7, cy - ry * 0.78,
      cx - rx * 1.0, cy - ry * 0.3,
      cx - rx * 0.98, cy
    );

    g.bezierCurveTo(
      cx - rx * 1.0, cy + ry * 0.3,
      cx - rx * 0.7, cy + ry * 0.78,
      cx - rx * 0.2, cy + ry * 0.92
    );

    g.bezierCurveTo(
      cx + rx * 0.25, cy + ry * 1.05,
      cx + rx, cy + ry * 0.75,
      cx + rx, cy
    );

    g.closePath();
  }

  function bakeAbdomen(rng) {
    var k = S * dpr;
    var w = Math.ceil(0.36 * k);
    var h = Math.ceil(0.28 * k);
    var c = mk(w, h);
    var g = c.getContext('2d');

    var cx = w / 2;
    var cy = h / 2;
    var i;

    abdPath(g, w, h);

    var gr = g.createRadialGradient(
      cx + w * 0.06,
      cy - h * 0.06,
      h * 0.04,
      cx,
      cy,
      w * 0.52
    );

    gr.addColorStop(0, '#5d452c');
    gr.addColorStop(0.5, '#38261a');
    gr.addColorStop(0.85, '#1c130c');
    gr.addColorStop(1, '#080503');

    g.fillStyle = gr;
    g.fill();

    g.save();
    g.clip();

    for (i = 0; i < 170; i++) {
      var a = 0.05 + rng() * 0.12;
      var px = cx + (rng() * 2 - 1) * w * 0.46;
      var py = cy + (rng() * 2 - 1) * h * 0.46;
      var r = (0.012 + rng() * 0.04) * k;

      g.fillStyle = rng() < 0.5
        ? 'rgba(170,135,92,' + a + ')'
        : 'rgba(0,0,0,' + (a + 0.05) + ')';

      g.beginPath();
      g.ellipse(
        px,
        py,
        r,
        r * (0.5 + rng() * 0.5),
        rng() * PI,
        0,
        TAU
      );
      g.fill();
    }

    g.lineJoin = 'round';
    g.lineCap = 'round';

    for (i = 0; i < 6; i++) {
      var x = cx + w * (0.28 - i * 0.095);
      var hw = h * (0.30 - i * 0.034);
      var dx = w * 0.075;

      g.beginPath();
      g.moveTo(x - dx, cy - hw);
      g.lineTo(x, cy);
      g.lineTo(x - dx, cy + hw);

      g.strokeStyle = 'rgba(0,0,0,0.35)';
      g.lineWidth = h * 0.075;
      g.stroke();

      g.strokeStyle = 'rgba(206,172,122,0.30)';
      g.lineWidth = h * 0.045;
      g.stroke();
    }

    hairBurst(g, rng, 1100, cx, cy, w * 0.47, h * 0.46, 0.022 * k, PI, 1.4);

    var rim = g.createRadialGradient(
      cx, cy, h * 0.22,
      cx, cy, w * 0.5
    );

    rim.addColorStop(0, 'rgba(0,0,0,0)');
    rim.addColorStop(0.7, 'rgba(0,0,0,0.05)');
    rim.addColorStop(1, 'rgba(0,0,0,0.5)');

    g.fillStyle = rim;
    g.fillRect(0, 0, w, h);

    g.restore();

    return c;
  }

  function eye(g, x, y, r) {
    g.fillStyle = '#0b0705';
    g.beginPath();
    g.arc(x, y, r * 1.25, 0, TAU);
    g.fill();

    var e = g.createRadialGradient(
      x - r * 0.3,
      y - r * 0.3,
      r * 0.1,
      x,
      y,
      r
    );

    e.addColorStop(0, '#3a3a3a');
    e.addColorStop(0.45, '#080808');
    e.addColorStop(1, '#000');

    g.fillStyle = e;
    g.beginPath();
    g.arc(x, y, r, 0, TAU);
    g.fill();

    g.fillStyle = 'rgba(255,255,255,0.85)';
    g.beginPath();
    g.arc(x - r * 0.32, y - r * 0.34, r * 0.26, 0, TAU);
    g.fill();
  }

  function bakeProsoma(rng) {
    var k = S * dpr;
    var w = Math.ceil(0.30 * k);
    var h = Math.ceil(0.20 * k);
    var c = mk(w, h);
    var g = c.getContext('2d');

    var cx = 0.40 * w;
    var cy = h / 2;
    var rx = 0.095 * k;
    var ry = 0.078 * k;
    var s, i;

    g.lineCap = 'round';

    for (s = -1; s <= 1; s += 2) {
      var bx = cx + rx * 0.78;
      var by = cy + s * ry * 0.30;
      var tx = cx + rx + 0.045 * k;
      var ty = cy + s * ry * 0.22;

      g.strokeStyle = '#2a160c';
      g.lineWidth = 0.026 * k;
      g.beginPath();
      g.moveTo(bx, by);
      g.lineTo(tx, ty);
      g.stroke();

      g.strokeStyle = '#6a4328';
      g.lineWidth = 0.010 * k;
      g.beginPath();
      g.moveTo(bx - 0.004 * k, by - 0.006 * k);
      g.lineTo(tx - 0.004 * k, ty - 0.006 * k);
      g.stroke();

      g.strokeStyle = '#100803';
      g.lineWidth = 0.008 * k;
      g.beginPath();
      g.moveTo(tx, ty);
      g.quadraticCurveTo(
        tx + 0.014 * k,
        ty - s * 0.002 * k,
        tx + 0.016 * k,
        cy + s * ry * 0.10
      );
      g.stroke();
    }

    g.beginPath();
    g.moveTo(cx + rx, cy);

    g.bezierCurveTo(
      cx + rx, cy - ry * 0.62,
      cx + rx * 0.35, cy - ry * 1.02,
      cx - rx * 0.15, cy - ry
    );

    g.bezierCurveTo(
      cx - rx * 0.75, cy - ry * 0.98,
      cx - rx, cy - ry * 0.55,
      cx - rx, cy
    );

    g.bezierCurveTo(
      cx - rx, cy + ry * 0.55,
      cx - rx * 0.75, cy + ry * 0.98,
      cx - rx * 0.15, cy + ry
    );

    g.bezierCurveTo(
      cx + rx * 0.35, cy + ry * 1.02,
      cx + rx, cy + ry * 0.62,
      cx + rx, cy
    );

    g.closePath();

    var gr = g.createRadialGradient(
      cx + rx * 0.1,
      cy,
      ry * 0.1,
      cx,
      cy,
      rx * 1.05
    );

    gr.addColorStop(0, '#6b4a2e');
    gr.addColorStop(0.6, '#3d2815');
    gr.addColorStop(1, '#140c06');

    g.fillStyle = gr;
    g.fill();

    g.save();
    g.clip();

    g.fillStyle = 'rgba(206,172,122,0.16)';
    g.beginPath();
    g.ellipse(cx + rx * 0.1, cy, rx * 0.85, ry * 0.26, 0, 0, TAU);
    g.fill();

    g.strokeStyle = 'rgba(0,0,0,0.38)';
    g.lineWidth = Math.max(1, 0.006 * k);

    for (i = 0; i < 6; i++) {
      var a = (i - 2.5) * 0.5;

      g.beginPath();
      g.moveTo(cx - rx * 0.12, cy);
      g.lineTo(
        cx - rx * 0.12 - Math.cos(a) * rx * 0.2 + rx * 0.5 * Math.cos(a),
        cy + Math.sin(a) * ry * 1.1
      );
      g.stroke();
    }

    g.fillStyle = 'rgba(0,0,0,0.55)';
    g.beginPath();
    g.ellipse(cx - rx * 0.12, cy, rx * 0.16, ry * 0.07, 0, 0, TAU);
    g.fill();

    hairBurst(g, rng, 380, cx, cy, rx, ry, 0.016 * k, PI, 1.6);

    var rim = g.createRadialGradient(
      cx, cy, ry * 0.4,
      cx, cy, rx * 1.1
    );

    rim.addColorStop(0, 'rgba(0,0,0,0)');
    rim.addColorStop(1, 'rgba(0,0,0,0.45)');

    g.fillStyle = rim;
    g.fillRect(0, 0, w, h);

    g.restore();

    // Eyes: 4 + 2 + 2
    var ex = [-0.5, -0.17, 0.17, 0.5];
    var r0 = 0.0125 * k;

    for (i = 0; i < 4; i++) {
      eye(
        g,
        cx + rx * 0.80,
        cy + ex[i] * ry * 0.55,
        i === 1 || i === 2 ? r0 : r0 * 0.7
      );
    }

    for (s = -1; s <= 1; s += 2) {
      eye(g, cx + rx * 0.55, cy + s * ry * 0.62, 0.010 * k);
      eye(g, cx + rx * 0.28, cy + s * ry * 0.64, 0.0095 * k);
    }

    return c;
  }

  function applyImage(canvas, url) {
    if (!url) return;

    var im = new Image();

    im.onload = function () {
      var g = canvas.getContext('2d');
      g.clearRect(0, 0, canvas.width, canvas.height);
      g.drawImage(im, 0, 0, canvas.width, canvas.height);
    };

    im.src = url;
  }

  function bake() {
    var rng = mulberry(20260612);

    abdC = bakeAbdomen(rng);
    proC = bakeProsoma(rng);

    applyImage(abdC, cfg.abdomenImage);
    applyImage(proC, cfg.prosomaImage);

    if (!hairs.length) {
      var i, b, j;

      for (i = 0; i < 8; i++) {
        var leg = [];

        for (b = 0; b < 3; b++) {
          var arr = [];
          var hc = HAIRCFG[b];

          for (j = 0; j < hc.n; j++) {
            arr.push({
              t: 0.04 + rng() * 0.92,
              s: rng() < 0.5 ? -1 : 1,
              a: rng() * 0.9 - 0.1,
              l: lerp(hc.l0, hc.l1, rng()),
              c: rng() < 0.4
            });
          }

          leg.push(arr);
        }

        hairs.push(leg);
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Drawing primitives                                                  */
  /* ------------------------------------------------------------------ */
  function tube(g, x0, y0, x1, y1, r0, r1, col, ox, oy) {
    var dx = x1 - x0;
    var dy = y1 - y0;
    var d = Math.sqrt(dx * dx + dy * dy) || 1;
    var nx = -dy / d;
    var ny = dx / d;

    x0 += ox;
    x1 += ox;
    y0 += oy;
    y1 += oy;

    g.fillStyle = col;
    g.beginPath();

    g.moveTo(x0 + nx * r0, y0 + ny * r0);
    g.lineTo(x1 + nx * r1, y1 + ny * r1);
    g.lineTo(x1 - nx * r1, y1 - ny * r1);
    g.lineTo(x0 - nx * r0, y0 - ny * r0);

    g.closePath();
    g.fill();

    g.beginPath();
    g.moveTo(x0 + r0, y0);
    g.arc(x0, y0, r0, 0, TAU);

    g.moveTo(x1 + r1, y1);
    g.arc(x1, y1, r1, 0, TAU);

    g.fill();
  }

  function limb(g, x0, y0, x1, y1, r0, r1) {
    tube(g, x0, y0, x1, y1, r0, r1, C_DARK, 0, 0);
    tube(g, x0, y0, x1, y1, r0 * 0.66, r1 * 0.66, C_MID, -r0 * 0.14, -r0 * 0.14);
    tube(g, x0, y0, x1, y1, r0 * 0.22, r1 * 0.22, C_LITE, -r0 * 0.30, -r0 * 0.30);
  }

  function disc(g, x, y, r, col) {
    g.fillStyle = col;
    g.beginPath();
    g.arc(x, y, r, 0, TAU);
    g.fill();
  }

  function drawHairs(g, x0, y0, x1, y1, r0, r1, arr) {
    var dx = x1 - x0;
    var dy = y1 - y0;
    var d = Math.sqrt(dx * dx + dy * dy) || 1;
    var ux = dx / d;
    var uy = dy / d;
    var nx = -uy;
    var ny = ux;

    var dk = [], lt = [], i;

    for (i = 0; i < arr.length; i++) {
      var h = arr[i];
      var t = h.t;
      var r = lerp(r0, r1, t);

      var bx = x0 + dx * t + nx * h.s * r * 0.8;
      var by = y0 + dy * t + ny * h.s * r * 0.8;

      var ca = Math.cos(h.a);
      var sa = Math.sin(h.a);
      var L = h.l * S;

      (h.c ? lt : dk).push(
        bx,
        by,
        bx + (nx * h.s * ca + ux * sa) * L,
        by + (ny * h.s * ca + uy * sa) * L
      );
    }

    g.lineCap = 'round';
    g.lineWidth = 0.7;
    strokeArr(g, dk, 'rgba(14,9,5,0.9)');

    g.lineWidth = 0.55;
    strokeArr(g, lt, 'rgba(135,105,68,0.6)');
  }

  /* ------------------------------------------------------------------ */
  /* Spider construction                                                 */
  /* ------------------------------------------------------------------ */
  function basis() {
    sp.cs = Math.cos(sp.h);
    sp.sn = Math.sin(sp.h);

    var sw = sp.sway || 0;

    sp.px = sp.x - sp.sn * sw;
    sp.py = sp.y + sp.cs * sw;
  }

  function anchors(leg, i) {
    var n = i & 3;
    var side = i < 4 ? -1 : 1;
    var hl = HIPS[n];
    var rl = REST[n];
    var cs = sp.cs;
    var sn = sp.sn;

    var lx = hl[0] * S;
    var ly = side * hl[1] * S;

    leg.hx = sp.px + lx * cs - ly * sn;
    leg.hy = sp.py + lx * sn + ly * cs;

    lx = rl[0] * S;
    ly = side * rl[1] * S;

    leg.rx = sp.px + lx * cs - ly * sn;
    leg.ry = sp.py + lx * sn + ly * cs;
  }

  function placeFeet() {
    basis();

    for (var i = 0; i < 8; i++) {
      var leg = sp.legs[i];

      anchors(leg, i);

      leg.x = leg.rx;
      leg.y = leg.ry;
      leg.stepping = false;
      leg.lift = 0;
    }
  }

  function inside(m) {
    return sp.x > m && sp.x < W - m && sp.y > m && sp.y < H - m;
  }

  function outside(m) {
    return sp.x < -m || sp.x > W + m || sp.y < -m || sp.y > H + m;
  }

  function pickWaypoint(far) {
    var m = Math.max(0.4 * S, 0.06 * Math.min(W, H));
    var best = null;
    var bd = -1;
    var mn = Math.min(W, H);
    var i;

    for (i = 0; i < 10; i++) {
      var x = rand(m, W - m);
      var y = rand(m, H - m);
      var d = Math.hypot(x - sp.x, y - sp.y);

      if (d > bd) {
        bd = d;
        best = [x, y];
      }

      if (d > (far ? 0.45 : 0.3) * mn) {
        best = [x, y];
        break;
      }
    }

    sp.wx = best[0];
    sp.wy = best[1];
  }

  function begin() {
    var side = (Math.random() * 4) | 0;
    var m = 0.95 * S;
    var x, y;

    if (side === 0) {
      x = -m;
      y = rand(0.15, 0.85) * H;
    } else if (side === 1) {
      x = W + m;
      y = rand(0.15, 0.85) * H;
    } else if (side === 2) {
      x = rand(0.15, 0.85) * W;
      y = -m;
    } else {
      x = rand(0.15, 0.85) * W;
      y = H + m;
    }

    sp = {
      x: x,
      y: y,
      h: 0,
      speed: 0,
      turnV: 0,
      abd: 0,
      gait: 0,
      t: 0,
      seed: rand(0, 100),
      sway: 0,
      px: x,
      py: y,
      cs: 1,
      sn: 0,
      vx: 0,
      vy: 0,
      legs: [],
      phase: 'enter',
      beh: 'enter',
      timer: 1e9,
      targetSpeed: 0,
      queue: [],
      fixedWant: null,
      roamLeft: rand(cfg.roamMin, cfg.roamMax),
      wx: 0,
      wy: 0,
      want: 0,
      recover: false,
      style: '',
      phaseT: 0,

      // Mouse interaction state
      mouseWatching: false,
      savedTargetSpeed: 0
    };

    pickWaypoint(true);

    sp.h = Math.atan2(sp.wy - y, sp.wx - x);

    var dashIn = Math.random() < 0.65;

    sp.style = dashIn ? 'dash' : 'creep';
    sp.speed = dashIn ? 2.2 * S : 0.6 * S;
    sp.targetSpeed = dashIn ? rand(2.4, 3.2) * S : 0.7 * S;

    for (var i = 0; i < 8; i++) {
      sp.legs.push({
        x: 0,
        y: 0,
        fx: 0,
        fy: 0,
        tx: 0,
        ty: 0,
        hx: 0,
        hy: 0,
        rx: 0,
        ry: 0,
        t: 0,
        dur: 0.1,
        stepping: false,
        lift: 0
      });
    }

    placeFeet();

    vis.style.visibility = 'visible';

    // The greeting becomes visible on the first rendered frame (see render()),
    // already placed above the spider, so it never flashes at a wrong position.
    if (greet) {
      greet.style.opacity = '0';
    }

    running = true;
    paused = false;
    last = performance.now();
    raf = requestAnimationFrame(frame);
  }

  function finish(reschedule) {
    running = false;
    paused = false;

    if (raf) cancelAnimationFrame(raf);
    raf = 0;

    sp = null;

    if (vis) vis.style.visibility = 'hidden';

    // Hide the greeting together with the spider.
    if (greet) {
      greet.style.visibility = 'hidden';
      greet.style.opacity = '0';
    }

    if (reschedule && cfg.repeat && !isDisabled() && !destroyed) {
      schedule(cfg.repeatDelayMin, cfg.repeatDelayMax);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Behaviour                                                          */
  /* ------------------------------------------------------------------ */
  function setB(k) {
    sp.beh = k;
    sp.fixedWant = null;

    switch (k) {
      case 'walk':
        sp.targetSpeed = rand(0.7, 1.5) * S;
        sp.timer = rand(1.4, 3.6);
        break;

      case 'creep':
        sp.targetSpeed = rand(0.25, 0.5) * S;
        sp.timer = rand(1.6, 3.2);
        break;

      case 'pause':
        sp.targetSpeed = 0;
        sp.timer = rand(0.6, 2.4);
        break;

      case 'dash':
        sp.targetSpeed = rand(2.6, 3.5) * S;
        sp.timer = rand(0.5, 1.1);
        pickWaypoint(true);
        break;

      case 'freeze':
        sp.targetSpeed = 0;
        sp.timer = rand(1.0, 2.3);
        sp.queue.push('dash');
        break;

      case 'turn':
        sp.targetSpeed = 0;
        sp.timer = rand(0.5, 1.0);
        sp.fixedWant = sp.h +
          (Math.random() < 0.5 ? -1 : 1) * rand(1.0, 2.4);
        break;
    }
  }

  function nextBehavior() {
    if (sp.queue.length) {
      setB(sp.queue.shift());
      return;
    }

    var r = Math.random();
    var prev = sp.beh;

    if (r < 0.34) setB('walk');
    else if (r < 0.50) setB('creep');
    else if (r < 0.66) setB(prev === 'pause' ? 'walk' : 'pause');
    else if (r < 0.80) setB('dash');
    else if (r < 0.90) setB('freeze');
    else setB('turn');
  }

  function beginExit() {
    sp.phase = 'exit';
    sp.beh = 'exit';
    sp.fixedWant = null;
    sp.queue.length = 0;
    sp.phaseT = 0;

    var dl = sp.x;
    var dr = W - sp.x;
    var dt = sp.y;
    var db = H - sp.y;
    var mn = Math.min(dl, dr, dt, db);
    var far = 3 * S;

    if (mn === dl) {
      sp.wx = -far;
      sp.wy = sp.y + rand(-0.15, 0.15) * H;
    } else if (mn === dr) {
      sp.wx = W + far;
      sp.wy = sp.y + rand(-0.15, 0.15) * H;
    } else if (mn === dt) {
      sp.wy = -far;
      sp.wx = sp.x + rand(-0.15, 0.15) * W;
    } else {
      sp.wy = H + far;
      sp.wx = sp.x + rand(-0.15, 0.15) * W;
    }

    sp.targetSpeed =
      (Math.random() < 0.7 ? rand(2.4, 3.3) : rand(1.0, 1.5)) * S;
  }

  function behave(dt) {
    sp.timer -= dt;
    sp.phaseT += dt;

    if (sp.phase === 'enter') {
      if (inside(0.15 * S) || sp.phaseT > 15) {
        sp.phase = 'roam';

        if (sp.style === 'dash') {
          sp.beh = 'dash';
          sp.timer = rand(0.3, 0.9);
        } else {
          setB('freeze');
        }
      }
    } else if (sp.phase === 'roam') {
      sp.roamLeft -= dt;

      if (sp.roamLeft <= 0) {
        beginExit();
      } else {
        if (sp.timer <= 0) nextBehavior();

        var dx = sp.wx - sp.x;
        var dy = sp.wy - sp.y;

        if (dx * dx + dy * dy < 0.25 * S * S) {
          pickWaypoint(false);
        }

        if (!inside(0.12 * S)) {
          if (!sp.recover) {
            sp.recover = true;
            sp.fixedWant = null;
            sp.wx = W * rand(0.3, 0.7);
            sp.wy = H * rand(0.3, 0.7);
          }
        } else {
          sp.recover = false;
        }

        // Occasional nervous front-leg twitch while paused
        if (sp.beh === 'pause' && sp.speed < 1 && Math.random() < dt * 1.1) {
          var li = [0, 4, 1, 5][(Math.random() * 4) | 0];
          var lg = sp.legs[li];

          if (!lg.stepping) {
            lg.fx = lg.x;
            lg.fy = lg.y;
            lg.tx = lg.rx + rand(-1, 1) * 0.05 * S;
            lg.ty = lg.ry + rand(-1, 1) * 0.05 * S;
            lg.t = 0;
            lg.dur = 0.18;
            lg.stepping = true;
          }
        }
      }
    } else if (sp.phase === 'exit') {
      if (outside(0.95 * S) || sp.phaseT > 14) {
        finish(true);
        return false;
      }
    }

    // Normal movement direction
    var base = Math.atan2(sp.wy - sp.y, sp.wx - sp.x);

    var wob = (
      Math.sin(sp.t * 1.3 + sp.seed) * 0.26 +
      Math.sin(sp.t * 3.1 + sp.seed * 1.7) * 0.12
    ) * clamp(sp.speed / S, 0, 1.2);

    sp.want = sp.fixedWant !== null ? sp.fixedWant : base + wob;

    // Mouse interaction:
    // Stop and face the pointer when it gets close.
    // Resume the previous target speed when the pointer moves away.
    var mdx = mouseX - sp.x;
    var mdy = mouseY - sp.y;
    var mouseDistance = Math.sqrt(mdx * mdx + mdy * mdy);

    var watchingMouse =
      sp.phase === 'roam' &&
      mouseDistance < 280;

    if (watchingMouse) {
      if (!sp.mouseWatching) {
        sp.savedTargetSpeed = sp.targetSpeed;
        sp.mouseWatching = true;
      }

      sp.targetSpeed = 0;
      sp.want = Math.atan2(mdy, mdx);

    } else if (sp.mouseWatching) {
      sp.mouseWatching = false;
      sp.targetSpeed = sp.savedTargetSpeed;
    }

    return true;
  }

  /* ------------------------------------------------------------------ */
  /* Physics: body, then legs                                            */
  /* ------------------------------------------------------------------ */
  function update(dt) {
    sp.t += dt;

    if (behave(dt) === false) return false;

    var diff = angDiff(sp.h, sp.want);
    var sN = clamp(sp.speed / (3.5 * S), 0, 1);
    var maxTurn = 6.0 - 4.0 * sN;
    var tt = clamp(diff * 4.5, -maxTurn, maxTurn);

    sp.turnV += (tt - sp.turnV) * Math.min(1, dt * 9);
    sp.h += sp.turnV * dt;

    var ts = sp.targetSpeed;

    if (ts > 0) {
      ts *= clamp(1 - Math.abs(diff) / 1.6, 0.12, 1);
    }

    var acc = (ts > sp.speed ? 9 : 15) * S;

    sp.speed += clamp(ts - sp.speed, -acc * dt, acc * dt);

    if (sp.speed < 0.4) sp.speed = 0;

    var c = Math.cos(sp.h);
    var s = Math.sin(sp.h);

    sp.vx = c * sp.speed;
    sp.vy = s * sp.speed;

    sp.x += sp.vx * dt;
    sp.y += sp.vy * dt;

    sp.gait += sp.speed * dt / (0.5 * S);

    var spN = clamp(sp.speed / S, 0, 1);

    sp.sway = Math.sin(sp.gait * TAU) * 0.006 * S * spN;

    var at =
      -sp.turnV * 0.05 +
      Math.sin(sp.gait * TAU) * 0.07 * spN +
      Math.sin(sp.t * 1.7) * 0.012;

    sp.abd += (at - sp.abd) * Math.min(1, dt * 8);

    basis();
    updateLegs(dt);

    return true;
  }

  function startStep(i, thr) {
    var leg = sp.legs[i];
    var st = sp.speed;

    var dur = clamp(
      0.35 * (2 * thr) / Math.max(st, 0.25 * S),
      0.07,
      0.2
    );

    var ra = sp.turnV * dur * 0.5;
    var c = Math.cos(ra);
    var s = Math.sin(ra);

    var ox = leg.rx - sp.px;
    var oy = leg.ry - sp.py;

    var tx = sp.px + ox * c - oy * s;
    var ty = sp.py + ox * s + oy * c;

    var lx = sp.vx * dur * 0.55;
    var ly = sp.vy * dur * 0.55;
    var ll = Math.sqrt(lx * lx + ly * ly);
    var cap = 0.2 * S;

    if (ll > cap) {
      lx *= cap / ll;
      ly *= cap / ll;
    }

    leg.fx = leg.x;
    leg.fy = leg.y;

    leg.tx = tx + lx + rand(-1, 1) * 0.012 * S;
    leg.ty = ty + ly + rand(-1, 1) * 0.012 * S;

    leg.t = 0;
    leg.dur = dur;
    leg.stepping = true;
  }

  function updateLegs(dt) {
    var i, leg, L = sp.legs;
    var st = sp.speed;
    var sN = clamp(st / (3.5 * S), 0, 1);

    var moving = st > 0.08 * S || Math.abs(sp.turnV) > 0.3;
    var thr = moving ? 0.13 * S * (1 + 0.8 * sN) : 0.07 * S;
    var cand = [];

    for (i = 0; i < 8; i++) {
      leg = L[i];
      anchors(leg, i);

      if (leg.stepping) {
        leg.t += dt / leg.dur;

        if (leg.t >= 1) {
          leg.stepping = false;
          leg.x = leg.tx;
          leg.y = leg.ty;
          leg.lift = 0;
        } else {
          var e = smooth(leg.t);
          leg.x = lerp(leg.fx, leg.tx, e);
          leg.y = lerp(leg.fy, leg.ty, e);
          leg.lift = Math.sin(PI * leg.t);
        }
      }

      if (!leg.stepping) {
        var dx = leg.x - leg.rx;
        var dy = leg.y - leg.ry;
        var str = Math.sqrt(dx * dx + dy * dy);

        if (str > thr) cand.push([i, str]);
      }
    }

    cand.sort(function (a, b) {
      return b[1] - a[1];
    });

    for (var c = 0; c < cand.length; c++) {
      i = cand[c][0];

      var n = i & 3;

      var busy =
        (n > 0 && L[i - 1].stepping) ||
        (n < 3 && L[i + 1].stepping) ||
        L[(i + 4) & 7].stepping;

      if (!busy || cand[c][1] > thr * 1.8) {
        startStep(i, thr);
      }
    }

    for (i = 0; i < 8; i++) {
      leg = L[i];

      var mr = LEN[i & 3] * S * 0.98;
      var ddx = leg.x - leg.hx;
      var ddy = leg.y - leg.hy;
      var d = Math.sqrt(ddx * ddx + ddy * ddy);

      if (d > mr) {
        leg.x = leg.hx + ddx / d * mr;
        leg.y = leg.hy + ddy / d * mr;
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Rendering                                                           */
  /* ------------------------------------------------------------------ */
  function drawLeg(g, i, leg) {
    var n = i & 3;
    var side = i < 4 ? -1 : 1;
    var len = LEN[n] * S;
    var a = len * 0.5;
    var lf = leg.lift;
    var px = sp.px;
    var py = sp.py;

    var hx = leg.hx;
    var hy = leg.hy;
    var dx = leg.x - hx;
    var dy = leg.y - hy;
    var dist = Math.sqrt(dx * dx + dy * dy) || 1e-3;

    var ux = dx / dist;
    var uy = dy / dist;
    var d = Math.min(dist, len * 0.995);
    var xx = d * 0.5;
    var h = Math.sqrt(Math.max(0, a * a - xx * xx));

    var mx = hx + ux * xx;
    var my = hy + uy * xx;
    var nx = -uy;
    var ny = ux;
    var ox = -sp.sn * side;
    var oy = sp.cs * side;

    var k1x = mx + nx * h;
    var k1y = my + ny * h;
    var k2x = mx - nx * h;
    var k2y = my - ny * h;
    var kx, ky;

    if (
      (k1x - px) * ox + (k1y - py) * oy >
      (k2x - px) * ox + (k2y - py) * oy
    ) {
      kx = k1x;
      ky = k1y;
    } else {
      kx = k2x;
      ky = k2y;
    }

    var ks = 1 + 0.09 * lf;
    kx = px + (kx - px) * ks;
    ky = py + (ky - py) * ks;

    var fs = 1 + 0.05 * lf;
    var fx = px + (leg.x - px) * fs;
    var fy = py + (leg.y - py) * fs;

    var ax = kx + (fx - kx) * 0.58;
    var ay = ky + (fy - ky) * 0.58;

    var rm = 1 + 0.16 * lf;
    var r = S;
    var hs = hairs[i];

    var rf0 = 0.0135 * r * rm;
    var rf1 = 0.0108 * r * rm;
    var rt0 = 0.0105 * r * rm;
    var rt1 = 0.0082 * r * rm;
    var rm0 = 0.0072 * r * rm;
    var rm1 = 0.0026 * r * rm;

    limb(g, hx, hy, kx, ky, rf0, rf1);
    drawHairs(g, hx, hy, kx, ky, rf0, rf1, hs[0]);

    limb(g, kx, ky, ax, ay, rt0, rt1);

    tube(
      g,
      lerp(kx, ax, 0.38),
      lerp(ky, ay, 0.38),
      lerp(kx, ax, 0.52),
      lerp(ky, ay, 0.52),
      lerp(rt0, rt1, 0.4),
      lerp(rt0, rt1, 0.5),
      'rgba(125,98,62,0.5)',
      0,
      0
    );

    drawHairs(g, kx, ky, ax, ay, rt0, rt1, hs[1]);

    limb(g, ax, ay, fx, fy, rm0, rm1);
    drawHairs(g, ax, ay, fx, fy, rm0, rm1, hs[2]);

    disc(g, kx, ky, 0.0128 * r * rm, C_DARK);
    disc(g, kx - 0.002 * r, ky - 0.002 * r, 0.0085 * r * rm, C_MID);
    disc(g, kx - 0.004 * r, ky - 0.004 * r, 0.003 * r * rm, C_LITE);
    disc(g, ax, ay, 0.0085 * r * rm, C_DARK);
  }

  function drawLegs(g, lifted) {
    for (var i = 0; i < 8; i++) {
      var leg = sp.legs[i];

      if ((leg.lift > 0.001) === lifted) {
        drawLeg(g, i, leg);
      }
    }
  }

  function drawPalps(g) {
    var cs = sp.cs;
    var sn = sp.sn;
    var k = S;
    var calm = sp.speed < 0.2 * S ? 1 : 0.35;

    function W2(lx, ly) {
      return [
        sp.px + lx * cs - ly * sn,
        sp.py + lx * sn + ly * cs
      ];
    }

    for (var s = -1; s <= 1; s += 2) {
      var tw =
        Math.sin(sp.t * 8.5 + s * 1.9) * 0.013 * k * calm +
        Math.sin(sp.t * 3.1 + s) * 0.006 * k;

      var p0 = W2(0.17 * k, s * 0.035 * k);
      var p1 = W2(0.235 * k, s * 0.062 * k + tw);
      var p2 = W2(0.272 * k, s * 0.038 * k + tw * 1.4);

      limb(g, p0[0], p0[1], p1[0], p1[1], 0.011 * k, 0.009 * k);
      limb(g, p1[0], p1[1], p2[0], p2[1], 0.009 * k, 0.006 * k);

      disc(g, p2[0], p2[1], 0.0085 * k, C_DARK);
      disc(g, p2[0] - 0.002 * k, p2[1] - 0.002 * k, 0.004 * k, C_MID);
    }
  }

  function render() {
    var ix = Math.round(sp.x);
    var iy = Math.round(sp.y);
    var g = bctx;
    var k = S;

    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, buf.width, buf.height);

    g.setTransform(
      dpr,
      0,
      0,
      dpr,
      (B / 2 - ix) * dpr,
      (B / 2 - iy) * dpr
    );

    drawLegs(g, false);
    drawLegs(g, true);
    drawPalps(g);

    g.save();
    g.translate(sp.px, sp.py);
    g.rotate(sp.h);

    var sc =
      1 +
      0.007 * Math.sin(sp.t * 2.1) +
      0.006 * Math.sin(sp.gait * TAU * 2) * clamp(sp.speed / S, 0, 1);

    g.scale(sc, sc);

    g.save();
    g.rotate(sp.abd);

    var aw = abdC.width / dpr;
    var ah = abdC.height / dpr;

    g.drawImage(
      abdC,
      -0.135 * k - aw / 2,
      -ah / 2,
      aw,
      ah
    );

    g.restore();

    var pw = proC.width / dpr;
    var ph = proC.height / dpr;

    g.drawImage(
      proC,
      0.085 * k - 0.40 * pw,
      -ph / 2,
      pw,
      ph
    );

    g.restore();

    // World-fixed lighting
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.globalCompositeOperation = 'source-atop';

    g.fillStyle = lightA;
    g.fillRect(0, 0, buf.width, buf.height);

    g.fillStyle = lightB;
    g.fillRect(0, 0, buf.width, buf.height);

    g.globalCompositeOperation = 'source-over';

    // Composite: soft shadow + tight contact shadow + spider
    var v = vctx;
    var FAR = vis.width * 2 + 1000;
    var kk = S * dpr;

    v.setTransform(1, 0, 0, 1, 0, 0);
    v.clearRect(0, 0, vis.width, vis.height);

    v.save();

    v.shadowColor = 'rgba(0,0,0,0.30)';
    v.shadowBlur = 0.06 * kk;
    v.shadowOffsetX = FAR + 0.04 * kk;
    v.shadowOffsetY = 0.06 * kk;
    v.drawImage(buf, -FAR, 0);

    v.shadowColor = 'rgba(0,0,0,0.55)';
    v.shadowBlur = 0.012 * kk;
    v.shadowOffsetX = FAR + 0.012 * kk;
    v.shadowOffsetY = 0.018 * kk;
    v.drawImage(buf, -FAR, 0);

    v.restore();

    v.drawImage(buf, 0, 0);

    vis.style.transform =
      'translate3d(' +
      (ix - B / 2) +
      'px,' +
      (iy - B / 2) +
      'px,0)';

    // Halloween greeting: updated on EVERY frame with the same coordinates
    // (ix, iy) that position the spider canvas.
    positionGreeting(ix, iy);
  }

  function frame(ts) {
    if (!running || paused) return;

    raf = requestAnimationFrame(frame);

    var dt = Math.min(0.05, (ts - last) / 1000);
    last = ts;

    if (dt <= 0) return;
    if (update(dt) === false) return;

    render();
  }

  /* ------------------------------------------------------------------ */
  /* Scheduling, enable/disable, lifecycle                              */
  /* ------------------------------------------------------------------ */
  function isDisabled() {
    if (doc.documentElement.hasAttribute('data-no-spider')) return true;
    if (/[?&]spider=off\b/.test(global.location.search)) return true;

    return load(cfg.storageKey) === 'off';
  }

  function reducedMotion() {
    return cfg.respectReducedMotion &&
      global.matchMedia &&
      global.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function schedule(min, max) {
    clearTimeout(timer);
    timer = setTimeout(appear, rand(min, max) * 1000);
  }

  function appear(force) {
    timer = 0;

    if (destroyed || sp) return;

    if (!force) {
      if (isDisabled() || reducedMotion()) return;
      if (cfg.oncePerSession && load(cfg.storageKey + '-seen', true)) return;
    }

    if (doc.hidden) {
      timer = setTimeout(function () {
        appear(force);
      }, 1000);
      return;
    }

    if (!doc.body) return;

    ensureDom();

    if (!built) build();

    if (cfg.oncePerSession) {
      store(cfg.storageKey + '-seen', '1', true);
    }

    begin();
  }

  function refreshToggle() {
    if (toggleBtn) {
      toggleBtn.textContent =
        'Spider effect: ' + (isDisabled() ? 'off' : 'on');
    }
  }

  function makeToggle() {
    toggleBtn = doc.createElement('button');
    toggleBtn.type = 'button';

    toggleBtn.style.cssText =
      'position:fixed;left:12px;bottom:12px;z-index:' + (cfg.zIndex - 1) +
      ';font:12px/1 system-ui,sans-serif;padding:7px 11px;border-radius:999px;' +
      'border:1px solid rgba(128,128,128,.5);' +
      'background:rgba(20,20,20,.78);color:#eee;cursor:pointer';

    toggleBtn.addEventListener('click', function () {
      if (isDisabled()) api.enable();
      else api.disable();
    });

    doc.body.appendChild(toggleBtn);
    refreshToggle();
  }

  function onResize() {
    clearTimeout(resizeT);

    resizeT = setTimeout(function () {
      // Re-measure the greeting on the next frame (font size changes on mobile).
      greetW = 0;
      greetH = 0;

      if (!built) return;

      var oldS = S;
      computeSize();

      if (Math.abs(oldS - S) > 1 && vis) {
        build();
        if (sp) placeFeet();
      } else {
        W = global.innerWidth;
        H = global.innerHeight;
      }
    }, 150);
  }

  function onVisibility() {
    if (!sp) return;

    if (doc.hidden) {
      paused = true;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    } else if (paused) {
      paused = false;
      last = performance.now();
      raf = requestAnimationFrame(frame);
    }
  }

  var api = {
    init: function (opts) {
      if (inited || destroyed) return;

      inited = true;

      cfg = {};

      var k;
      var src = [DEFAULTS, global.SpiderEffectConfig || {}, opts || {}];

      for (var s = 0; s < src.length; s++) {
        for (k in src[s]) {
          if (Object.prototype.hasOwnProperty.call(src[s], k)) {
            cfg[k] = src[s][k];
          }
        }
      }

      var go = function () {
        if (cfg.showToggle) makeToggle();

        global.addEventListener('resize', onResize);
        doc.addEventListener('visibilitychange', onVisibility);

        if (cfg.autoStart && !isDisabled() && !reducedMotion()) {
          schedule(cfg.delayMin, cfg.delayMax);
        }
      };

      if (doc.readyState === 'loading') {
        doc.addEventListener('DOMContentLoaded', go);
      } else {
        go();
      }
    },

    show: function () {
      if (!cfg) api.init({ autoStart: false });
      appear(true);
    },

    hide: function () {
      finish(false);
    },

    enable: function () {
      store(cfg.storageKey, null);
      refreshToggle();

      if (!sp && !destroyed && !reducedMotion() && !isDisabled()) {
        schedule(cfg.delayMin, cfg.delayMax);
      }
    },

    disable: function () {
      store(cfg.storageKey, 'off');
      clearTimeout(timer);
      timer = 0;
      finish(false);
      refreshToggle();
    },

    destroy: function () {
      destroyed = true;
      clearTimeout(timer);
      finish(false);

      global.removeEventListener('resize', onResize);
      doc.removeEventListener('visibilitychange', onVisibility);

      if (vis && vis.parentNode) {
        vis.parentNode.removeChild(vis);
      }

      // Remove the Halloween greeting and its styles.
      if (greet && greet.parentNode) {
        greet.parentNode.removeChild(greet);
      }

      if (greetStyle && greetStyle.parentNode) {
        greetStyle.parentNode.removeChild(greetStyle);
      }

      greet = null;
      greetStyle = null;

      if (toggleBtn && toggleBtn.parentNode) {
        toggleBtn.parentNode.removeChild(toggleBtn);
      }
    }
  };

  global.SpiderEffect = api;

  var me = doc.currentScript;

  if (!(me && me.hasAttribute('data-manual'))) {
    api.init();
  }
})(window, document);